import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit2, Trash2, RefreshCw, Settings, Eye, Brain, User, Calendar, TrendingUp } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import Loader from '../../components/ui/Loader';
import Badge from '../../components/ui/Badge';
import Card from '../../components/ui/Card';
import examenService from '../../services/examenService';
import perfilService from '../../services/perfilService';
import perfilPruebaService from '../../services/perfilPruebaService';
import discService from '../../services/discService';
import RevisionExamen from './RevisionExamen';

export default function SupervisorExamenes() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [examenes, setExamenes] = useState([]);
  const [perfiles, setPerfiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [examenRevision, setExamenRevision] = useState(null);
  const [testsDisc, setTestsDisc] = useState([]);
  const [perfilesPruebas, setPerfilesPruebas] = useState([]);
  const [searchTermDisc, setSearchTermDisc] = useState('');
  const [selectedTestDisc, setSelectedTestDisc] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Cargar perfiles primero
      let perfilesData = [];
      try {
        const perfilesResponse = await perfilService.getAllPerfiles({ page: 0, size: 1000 });
        console.log('Respuesta de perfiles:', perfilesResponse);
        
        // Manejar diferentes formatos de respuesta
        if (perfilesResponse && typeof perfilesResponse === 'object' && perfilesResponse.content) {
          // Respuesta paginada
          perfilesData = perfilesResponse.content;
        } else if (Array.isArray(perfilesResponse)) {
          // Respuesta como array directo
          perfilesData = perfilesResponse;
        } else {
          // Fallback
          perfilesData = [];
        }
        
        setPerfiles(perfilesData || []);
      } catch (perfilError) {
        console.error('Error cargando perfiles:', perfilError);
        toast.error('Error al cargar perfiles');
        setPerfiles([]);
      }

      // Cargar exámenes
      let examenesData = [];
      try {
        examenesData = await examenService.getAllExamenes();
        setExamenes(examenesData || []);
      } catch (examenError) {
        console.error('Error cargando exámenes:', examenError);
        if (examenError.response?.status === 400) {
          console.log('La API de exámenes devolvió 400. Esto es normal si no hay exámenes creados.');
          setExamenes([]);
        } else {
          toast.error('Error al cargar exámenes');
          setExamenes([]);
        }
      }

      // Cargar Tests DISC completados
      try {
        const testsDiscData = await discService.getTodosLosTestsCompletados();
        setTestsDisc(testsDiscData || []);
      } catch (discError) {
        console.error('Error cargando tests DISC:', discError);
        setTestsDisc([]);
      }

      // Cargar perfil-pruebas para obtener relaciones
      try {
        const perfilesPruebasData = await perfilPruebaService.getAll();
        setPerfilesPruebas(perfilesPruebasData || []);
      } catch (ppError) {
        console.error('Error cargando perfil-pruebas:', ppError);
        setPerfilesPruebas([]);
      }
    } catch (error) {
      console.error('Error general:', error);
      setExamenes([]);
      setPerfiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      // Solo para exámenes RAVEN (examenes es solo para Raven)
      if (editingItem) {
        await examenService.updateExamen(editingItem.id, data);
        toast.success('Examen Raven actualizado');
      } else {
        // Crear el examen
        const nuevoExamen = await examenService.createExamen(data);
        console.log('✅ Examen creado:', nuevoExamen);
        
        // Si se seleccionó un perfil, crear la asociación automáticamente
        if (data.perfil?.id) {
          try {
            console.log(`🔗 Asociando examen ${nuevoExamen.id} al perfil ${data.perfil.id}`);
            await perfilPruebaService.asociarExamenRaven(data.perfil.id, nuevoExamen.id);
            toast.success('Examen Raven creado y asociado al perfil');
          } catch (asociacionError) {
            console.error('Error al asociar examen al perfil:', asociacionError);
            toast.warning('Examen creado pero no se pudo asociar al perfil automáticamente');
          }
        } else {
          toast.success('Examen Raven creado');
        }
      }
      loadData();
      closeModal();
    } catch (error) {
      toast.error('Error al guardar');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este examen?')) return;
    
    try {
      await examenService.deleteExamen(id);
      toast.success('Examen eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
      console.error(error);
    }
  };

  const openModal = (item = null) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const filteredExamenes = examenes.filter(e =>
    e.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <Loader />;

  // Si hay un examen en revisión, mostrar el componente de revisión
  if (examenRevision) {
    return <RevisionExamen examenId={examenRevision} onVolver={() => setExamenRevision(null)} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-neutral-800">Exámenes</h1>
          <p className="text-neutral-600 mt-1">Gestión de exámenes de evaluación</p>
        </div>
        <Button onClick={() => openModal()} className="gap-2">
          <Plus className="w-4 h-4" />
          Nuevo Examen
        </Button>
      </div>

      {/* Navegación rápida a tipos de examen */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card 
          className="cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-blue-500"
          onClick={() => navigate('/supervisor/examenes/raven')}
        >
          <div className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Brain className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-neutral-800">Test Raven</h3>
            </div>
            <p className="text-sm text-neutral-600">Matrices progresivas y razonamiento abstracto</p>
          </div>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-purple-500"
          onClick={() => navigate('/supervisor/examenes/disc')}
        >
          <div className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <User className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-bold text-neutral-800">Test DISC</h3>
            </div>
            <p className="text-sm text-neutral-600">Evaluación de personalidad y comportamiento</p>
          </div>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-indigo-500"
          onClick={() => navigate('/supervisor/examenes/ipv')}
        >
          <div className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-lg font-bold text-neutral-800">Test IPV</h3>
            </div>
            <p className="text-sm text-neutral-600">Inventario de Personalidad para Vendedores</p>
          </div>
        </Card>
      </div>

      <div className="flex justify-between items-center gap-4">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Buscar exámenes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
          />
        </div>
        <Button variant="outline" onClick={loadData} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Nombre</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Descripción</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Perfil</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Estado</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Tiempo (min)</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Ponderación (%)</th>
              <th className="text-right p-4 text-sm font-semibold text-neutral-700">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {filteredExamenes.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-8 text-center text-neutral-500">
                  No se encontraron exámenes
                </td>
              </tr>
            ) : (
              filteredExamenes.map((examen) => {
                const getEstadoBadge = (estado) => {
                  const estados = {
                    'PENDIENTE': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', text: 'Pendiente' },
                    'PROGRESO': { color: 'bg-blue-100 text-blue-800 border-blue-200', text: 'En Progreso' },
                    'FINALIZADO': { color: 'bg-green-100 text-green-800 border-green-200', text: 'Finalizado' }
                  };
                  const config = estados[estado] || { color: 'bg-gray-100 text-gray-800 border-gray-200', text: estado || 'N/A' };
                  return (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>
                      {config.text}
                    </span>
                  );
                };

                return (
                  <tr key={examen.id} className="hover:bg-neutral-50">
                    <td className="p-4 font-medium text-neutral-800">{examen.nombre}</td>
                    <td className="p-4 text-neutral-600">{examen.descripcion}</td>
                    <td className="p-4 text-neutral-600">{examen.perfilNombre || examen.perfil?.nombre || 'N/A'}</td>
                    <td className="p-4">{getEstadoBadge(examen.estado)}</td>
                    <td className="p-4 text-neutral-600">{examen.tiempoMinutos || 'N/A'}</td>
                    <td className="p-4 text-neutral-600">{examen.porcentajePonderacion || 'N/A'}%</td>
                    <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setExamenRevision(examen.id)}
                        className="p-2 text-info hover:bg-info-50 rounded-md transition-colors"
                        title="Revisar respuestas"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/supervisor/examenes/gestionar/${examen.id}`)}
                        className="p-2 text-secondary hover:bg-secondary-50 rounded-md transition-colors"
                        title="Gestionar preguntas y opciones"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openModal(examen)}
                        className="p-2 text-primary hover:bg-primary-50 rounded-md transition-colors"
                        title="Editar examen"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(examen.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Eliminar examen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* SECCIÓN DE TESTS DISC */}
      <div className="mt-12">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
            <Brain className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-neutral-800">Tests DISC Completados</h2>
            <p className="text-neutral-600">Resultados de evaluaciones de personalidad</p>
          </div>
        </div>

        <div className="flex justify-between items-center gap-4 mb-4">
          <div className="flex-1 max-w-md">
            <Input
              placeholder="Buscar por usuario o DNI..."
              value={searchTermDisc}
              onChange={(e) => setSearchTermDisc(e.target.value)}
              icon={Search}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {testsDisc
            .filter(test => 
              test.usuarioNombre?.toLowerCase().includes(searchTermDisc.toLowerCase()) ||
              test.usuarioDni?.includes(searchTermDisc)
            )
            .map((testDisc) => (
            <Card key={testDisc.id} className="hover:shadow-lg transition-shadow">
              <div className="p-5 space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-5 h-5 text-purple-600" />
                      <h3 className="font-bold text-neutral-900">{testDisc.usuarioNombre}</h3>
                    </div>
                    <p className="text-sm text-neutral-600">DNI: {testDisc.usuarioDni}</p>
                  </div>
                  <Badge variant="primary" className="bg-gradient-to-r from-purple-600 to-pink-600">
                    {testDisc.perfilResultado}
                  </Badge>
                </div>

                {/* Puntuaciones DISC */}
                <div className="grid grid-cols-4 gap-2">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-center">
                    <p className="text-xs text-red-600 font-medium">D</p>
                    <p className="text-lg font-bold text-red-700">{testDisc.puntuacionD}</p>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 text-center">
                    <p className="text-xs text-yellow-600 font-medium">I</p>
                    <p className="text-lg font-bold text-yellow-700">{testDisc.puntuacionI}</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-center">
                    <p className="text-xs text-green-600 font-medium">S</p>
                    <p className="text-lg font-bold text-green-700">{testDisc.puntuacionS}</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-center">
                    <p className="text-xs text-blue-600 font-medium">C</p>
                    <p className="text-lg font-bold text-blue-700">{testDisc.puntuacionC}</p>
                  </div>
                </div>

                {/* Info adicional */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-neutral-600">
                    <TrendingUp className="w-4 h-4" />
                    <span>Segmento: <strong>{testDisc.segmento}</strong></span>
                  </div>
                  <div className="flex items-center gap-2 text-neutral-600">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(testDisc.fechaRealizacion).toLocaleString('es-PE')}</span>
                  </div>
                </div>

                {/* Botón ver detalles */}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setSelectedTestDisc(testDisc)}
                >
                  <Eye className="w-4 h-4" />
                  Ver Detalles
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {testsDisc.length === 0 && (
          <div className="text-center py-12 bg-neutral-50 rounded-lg border border-neutral-200">
            <Brain className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
            <p className="text-neutral-600">No hay tests DISC completados aún</p>
          </div>
        )}
      </div>

      {/* Modal de detalles del test DISC */}
      {selectedTestDisc && (
        <Modal
          isOpen={!!selectedTestDisc}
          onClose={() => setSelectedTestDisc(null)}
          title={`Test DISC - ${selectedTestDisc.usuarioNombre}`}
          size="xl"
        >
          <div className="space-y-6">
            {/* Resumen */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-neutral-600">DNI</p>
                <p className="font-semibold text-neutral-900">{selectedTestDisc.usuarioDni}</p>
              </div>
              <div>
                <p className="text-sm text-neutral-600">Perfil Resultado</p>
                <Badge variant="primary" className="bg-gradient-to-r from-purple-600 to-pink-600 mt-1">
                  {selectedTestDisc.perfilResultado}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-neutral-600">Segmento</p>
                <p className="font-semibold text-neutral-900">{selectedTestDisc.segmento}</p>
              </div>
              <div>
                <p className="text-sm text-neutral-600">Fecha</p>
                <p className="font-semibold text-neutral-900">
                  {new Date(selectedTestDisc.fechaRealizacion).toLocaleString('es-PE')}
                </p>
              </div>
            </div>

            {/* Puntuaciones detalladas */}
            <div>
              <h3 className="font-bold text-neutral-900 mb-3">Puntuaciones DISC</h3>
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-red-600 font-medium mb-2">Dominancia</p>
                  <p className="text-3xl font-bold text-red-700">{selectedTestDisc.puntuacionD}</p>
                </div>
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-yellow-600 font-medium mb-2">Influencia</p>
                  <p className="text-3xl font-bold text-yellow-700">{selectedTestDisc.puntuacionI}</p>
                </div>
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-green-600 font-medium mb-2">Estabilidad</p>
                  <p className="text-3xl font-bold text-green-700">{selectedTestDisc.puntuacionS}</p>
                </div>
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-blue-600 font-medium mb-2">Cumplimiento</p>
                  <p className="text-3xl font-bold text-blue-700">{selectedTestDisc.puntuacionC}</p>
                </div>
              </div>
            </div>

            {/* Descripciones */}
            <div className="space-y-3">
              <h3 className="font-bold text-neutral-900">Análisis del Perfil</h3>
              
              {selectedTestDisc.descripcionEmociones && (
                <div className="bg-neutral-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-neutral-700 mb-1">Emociones:</p>
                  <p className="text-neutral-900">{selectedTestDisc.descripcionEmociones}</p>
                </div>
              )}
              
              {selectedTestDisc.descripcionMeta && (
                <div className="bg-neutral-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-neutral-700 mb-1">Meta:</p>
                  <p className="text-neutral-900">{selectedTestDisc.descripcionMeta}</p>
                </div>
              )}
              
              {selectedTestDisc.descripcionJuzga && (
                <div className="bg-neutral-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-neutral-700 mb-1">Juzga a otros por:</p>
                  <p className="text-neutral-900">{selectedTestDisc.descripcionJuzga}</p>
                </div>
              )}
              
              {selectedTestDisc.descripcionInfluye && (
                <div className="bg-neutral-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-neutral-700 mb-1">Influye mediante:</p>
                  <p className="text-neutral-900">{selectedTestDisc.descripcionInfluye}</p>
                </div>
              )}
              
              {selectedTestDisc.descripcionValor && (
                <div className="bg-neutral-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-neutral-700 mb-1">Su valor para la organización:</p>
                  <p className="text-neutral-900">{selectedTestDisc.descripcionValor}</p>
                </div>
              )}
              
              {selectedTestDisc.descripcionTeme && (
                <div className="bg-neutral-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-neutral-700 mb-1">Teme:</p>
                  <p className="text-neutral-900">{selectedTestDisc.descripcionTeme}</p>
                </div>
              )}
              
              {selectedTestDisc.descripcionEficaz && (
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <p className="text-sm font-medium text-green-700 mb-1">Sería más eficaz si:</p>
                  <p className="text-neutral-900">{selectedTestDisc.descripcionEficaz}</p>
                </div>
              )}
            </div>

            {/* Archivo Excel si existe */}
            {selectedTestDisc.archivoExcelPath && (
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <p className="text-sm font-medium text-blue-700 mb-1">Archivo de resultados:</p>
                <p className="text-neutral-900">{selectedTestDisc.archivoExcelPath}</p>
              </div>
            )}
          </div>
        </Modal>
      )}

      <ExamenModal
        isOpen={showModal}
        onClose={closeModal}
        onSave={handleSave}
        initialData={editingItem}
        perfiles={perfiles}
      />
    </div>
  );
}

function ExamenModal({ isOpen, onClose, onSave, initialData, perfiles }) {
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    tiempoMinutos: '',
    porcentajePonderacion: '',
    estado: 'PENDIENTE',
    tipoExamen: 'RAVEN', // Solo RAVEN (examenes = raven)
    perfil: { id: '' }
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        nombre: initialData.nombre || '',
        descripcion: initialData.descripcion || '',
        tiempoMinutos: initialData.tiempoMinutos || '',
        porcentajePonderacion: initialData.porcentajePonderacion || '',
        estado: initialData.estado || 'PENDIENTE',
        tipoExamen: 'RAVEN', // Siempre RAVEN en esta tabla
        perfil: { id: initialData.perfilId || initialData.perfil?.id || '' }
      });
    } else {
      setFormData({
        nombre: '',
        descripcion: '',
        tiempoMinutos: '',
        porcentajePonderacion: '',
        estado: 'PENDIENTE',
        tipoExamen: 'RAVEN',
        perfil: { id: '' }
      });
    }
  }, [initialData, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      tiempoMinutos: parseInt(formData.tiempoMinutos),
      porcentajePonderacion: parseFloat(formData.porcentajePonderacion),
      estado: initialData ? formData.estado : 'PENDIENTE' // Al crear: PENDIENTE, al editar: mantener estado actual
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? 'Editar Examen Raven' : 'Nuevo Examen Raven'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Info: Solo exámenes Raven aquí */}
        <div className="p-3 rounded-md text-sm bg-blue-50 text-blue-800 border border-blue-200">
          <div className="flex items-start gap-2">
            <span className="text-lg">🧩</span>
            <div>
              <strong>Exámenes Raven:</strong> Esta sección es solo para crear exámenes tipo Raven (matrices progresivas).
              <div className="mt-2 text-xs">
                💡 <strong>Para Test DISC:</strong> Ve a <strong>"Perfil - Pruebas"</strong> en el menú lateral para asociar Tests DISC a perfiles.
              </div>
            </div>
          </div>
        </div>

        <Input
          label="Nombre"
          value={formData.nombre}
          onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
          required
          placeholder="Ej: Test Raven Avanzado"
        />
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Descripción
          </label>
          <textarea
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            rows={2}
            value={formData.descripcion}
            onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
            required
            placeholder="Evaluación de razonamiento abstracto mediante matrices progresivas"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Perfil
          </label>
          <select
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            value={formData.perfil.id}
            onChange={(e) => setFormData({ ...formData, perfil: { id: parseInt(e.target.value) } })}
            required
          >
            <option value="">Seleccionar perfil</option>
            {perfiles.map(p => (
              <option key={p.id} value={p.id}>{p.nombre}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Tiempo (minutos)"
            type="number"
            value={formData.tiempoMinutos}
            onChange={(e) => setFormData({ ...formData, tiempoMinutos: e.target.value })}
            required
            placeholder="45"
          />
          <Input
            label="Ponderación (%)"
            type="number"
            step="0.01"
            value={formData.porcentajePonderacion}
            onChange={(e) => setFormData({ ...formData, porcentajePonderacion: e.target.value })}
            required
            placeholder="100"
          />
        </div>

        {/* Instrucciones finales */}
        <div className="p-3 rounded-md text-sm bg-blue-50 text-blue-800 border border-blue-200">
          <strong>📝 Siguiente paso:</strong> Después de crear el examen, usa el botón <strong>"Gestionar"</strong> (⚙️) 
          en la tabla para agregar las preguntas y opciones del Test Raven.
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit">
            Guardar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
